import { OWNER_REFERENCE_KEYS, parseVesselParticulars } from '@felix/domain';
import { z } from 'zod';
import type { VesselParticulars, VesselParticularsInput } from '../pms/types.js';

/**
 * Gemi özellikleri formu (Fleet ▸ Vessels; plan M7 Task 6c): React Hook Form + Zod. Kurallar
 * domain'in `parseVesselParticulars`'ından (yapım yılı gelecekte olamaz); sunucu yeniden denetler.
 * Hata iletisi domain kodudur (`pms.fieldErrors.<KOD>`).
 */
export interface ParticularsFormValues {
  vesselType: string;
  builtYear: number | '';
  flag: string;
  classSociety: string;
  classNo: string;
  callSign: string;
  /** Risk ana verisinin gemi grubu (ADR 0086 §4); boş: yalnız ALL. */
  raGroup: string;
  /** Gross tonnage (ADR 0117 §8): ondalık metin; boş: bilinmiyor. */
  grossTonnage: string;
  /** ISM sorumlusu şirket (ADR 0117 §8). */
  ismCompany: string;
  /** Owner's reference (ADR 0084 Ek 2): charter'sız seferin hız ve tüketimi; boş: yok. */
  ownerSpeedLadenKn: number | '';
  ownerSpeedBallastKn: number | '';
  ownerConsumptionLadenMt: number | '';
  ownerConsumptionBallastMt: number | '';
}

/** Owner's reference alanı → form alanı (`speedLadenKn` → `ownerSpeedLadenKn`). */
export const OWNER_FIELDS = {
  speedLadenKn: 'ownerSpeedLadenKn',
  speedBallastKn: 'ownerSpeedBallastKn',
  consumptionLadenMt: 'ownerConsumptionLadenMt',
  consumptionBallastMt: 'ownerConsumptionBallastMt',
} as const satisfies Record<(typeof OWNER_REFERENCE_KEYS)[number], keyof ParticularsFormValues>;

const text = (value: string | null) => value ?? '';
const blank = (value: string) => (value.trim() === '' ? null : value);

export function particularsFormOf(particulars: VesselParticulars): ParticularsFormValues {
  return {
    vesselType: text(particulars.vesselType),
    builtYear: particulars.builtYear ?? '',
    flag: text(particulars.flag),
    classSociety: text(particulars.classSociety),
    classNo: text(particulars.classNo),
    callSign: text(particulars.callSign),
    raGroup: text(particulars.raGroup ?? null),
    grossTonnage: text(particulars.grossTonnage ?? null),
    ismCompany: text(particulars.ismCompany ?? null),
    ownerSpeedLadenKn: particulars.ownerReference?.speedLadenKn ?? '',
    ownerSpeedBallastKn: particulars.ownerReference?.speedBallastKn ?? '',
    ownerConsumptionLadenMt: particulars.ownerReference?.consumptionLadenMt ?? '',
    ownerConsumptionBallastMt: particulars.ownerReference?.consumptionBallastMt ?? '',
  };
}

export function toParticularsInput(values: ParticularsFormValues): VesselParticularsInput {
  return {
    vesselType: blank(values.vesselType),
    builtYear: values.builtYear === '' ? null : values.builtYear,
    flag: blank(values.flag),
    classSociety: blank(values.classSociety),
    classNo: blank(values.classNo),
    callSign: blank(values.callSign),
    raGroup: blank(values.raGroup),
    grossTonnage: blank(values.grossTonnage.trim()),
    ismCompany: blank(values.ismCompany),
    ownerReference: ownerReferenceInput(values),
  };
}

function ownerReferenceInput(values: ParticularsFormValues) {
  const out = Object.fromEntries(
    OWNER_REFERENCE_KEYS.map((key) => {
      const value = values[OWNER_FIELDS[key]];
      return [key, value === '' ? null : value];
    }),
  ) as Record<(typeof OWNER_REFERENCE_KEYS)[number], number | null>;
  return Object.values(out).some((v) => v !== null) ? out : null;
}

/** `year`: bugünün yılı (yapım yılı gelecekte olamaz; CLAUDE.md "Zaman"). */
export function particularsFormSchema(year: number) {
  return z
    .object({
      vesselType: z.string(),
      builtYear: z.union([z.number(), z.literal('')]),
      flag: z.string(),
      classSociety: z.string(),
      classNo: z.string(),
      callSign: z.string(),
      raGroup: z.string(),
      grossTonnage: z.string(),
      ismCompany: z.string(),
      ownerSpeedLadenKn: z.union([z.number(), z.literal('')]),
      ownerSpeedBallastKn: z.union([z.number(), z.literal('')]),
      ownerConsumptionLadenMt: z.union([z.number(), z.literal('')]),
      ownerConsumptionBallastMt: z.union([z.number(), z.literal('')]),
    })
    .superRefine((values, ctx) => {
      const parsed = parseVesselParticulars(toParticularsInput(values), { year });
      if (parsed.ok) return;
      for (const issue of parsed.issues) {
        const [head, key] = issue.path.split('.');
        // Owner's reference alanı formda düz alandır
        const path =
          head === 'ownerReference' && key && key in OWNER_FIELDS
            ? [OWNER_FIELDS[key as keyof typeof OWNER_FIELDS]]
            : issue.path.split('.');
        ctx.addIssue({ code: 'custom', message: issue.code, path });
      }
    });
}
